import * as React from 'react'
import { useForm } from 'react-hook-form'
import {
  addMonths,
  endOfDay,
  startOfDay,
  eachDayOfInterval,
  eachMinuteOfInterval,
} from 'date-fns'

import Presenter, {
  IProps as IPresenter,
  REQUEST_TYPE_SITTER,
  REQUEST_TYPE_PICKUP_ONLY,
  REQUEST_TYPE_SICK,
  REQUEST_TYPE_ILLNESS,
  REQUEST_TYPE_HANDICAP,
  REQUEST_TYPE_DOULA,
  REQUEST_TYPE_TUTOR,
  REQUEST_TYPE_HOUSEKEEPING,
  RESERVE_PLAN_CASUAL_INTERVIEW,
  RESERVE_PLAN_INTERVIEW,
  RESERVE_PLAN_SOPT,
  RESERVE_PLAN_TOKYO_BS_TMP_USE,
  SUPPORT_STYLE_OFFLINE,
  SUPPORT_STYLE_ONLINE,
} from './presenter'
import {
  parseDateStringToDate,
  dateToStringWithFormat,
} from '@/utils/dateFormat'

interface IProps
  extends Omit<
    IPresenter,
    | 'defaultStartDate'
    | 'defaultEndDate'
    | 'dateRange'
    | 'hourRange'
    | 'showTutorInformation'
    | 'showInterview'
    | 'canSpotRequst'
    | 'canTokyoBsTmpUse'
    | 'showSupportStyle'
    | 'supportStyle'
    | 'showOnloneSupportInformation'
    | 'showTokyoBsInformation'
    | 'inputCasualInterviewFlag'
    | 'inputInterviewFlag'
    | 'inputTokyoBsTmpUse'
    | 'inputComment'
    | 'register'
    | 'changeRequestTypeHandler'
    | 'changeReserveTypeHandler'
    | 'changeSupportStyleHandler'
    | 'clickExampleCommentHandler'
    | 'submitHandler'
    | 'cancelHandler'
  > {
  submitURL: string
  cancelURL: string
  startAt: string
  endAt: string
  // csrf token
  authenticityToken: string
  // 面談が必要か
  needInterview: boolean
  // 面談の予約ができるどうか
  availableInterviewRequest: boolean
  // サポータープロフィール
  sitterProfile: IReserveSitterProfile
  // 初期入力内容
  initialRequestType: IRequestType
  initialReservePlan: 'spot_reserve'
  initialCasualInterviewFlag: number
  initialInterviewFlg: number
  initialTokyoBsTmpUse: number
  initialPersonAtHome: string
  initialComment: string
  initialVideoRecording: string
  initialPriceType: IPriceType
}

const isChildcare = (requestType: IRequestType) =>
  [
    REQUEST_TYPE_SITTER,
    REQUEST_TYPE_PICKUP_ONLY,
    REQUEST_TYPE_SICK,
    REQUEST_TYPE_ILLNESS,
    REQUEST_TYPE_HANDICAP,
    REQUEST_TYPE_DOULA,
    REQUEST_TYPE_TUTOR,
  ].includes(requestType)

export const TimeTmpHolding = ({
  submitURL,
  cancelURL,
  startAt,
  endAt,
  authenticityToken,
  needInterview,
  availableInterviewRequest,
  showHousekeepingSubsidy,
  disabledHousekeepingSubsidy,
  children,
  initialRequestType,
  initialReservePlan,
  initialCasualInterviewFlag,
  initialInterviewFlg,
  initialTokyoBsTmpUse,
  initialPersonAtHome,
  initialComment,
  initialVideoRecording,
  initialPriceType,
  ...props
}: IProps): JSX.Element => {
  // サポート日時の設定
  const start = new Date()
  const end = addMonths(start, 3)
  const dateRange = eachDayOfInterval({ start, end })
  const hourRange = eachMinuteOfInterval(
    { start: startOfDay(start), end: endOfDay(start) },
    { step: 15 },
  ).map((day) => dateToStringWithFormat(day, 'HH:mm'))
  const defaultStartDate = parseDateStringToDate(startAt)
  const defaultEndDate = parseDateStringToDate(endAt)
  const [canSubmit, setCanSubmit] = React.useState<boolean>(true)

  // フォーム設定
  const { setValue, handleSubmit, watch, register } =
    useForm<IReserveRequestForm>({
      defaultValues: {
        authenticity_token: authenticityToken,
        child: [],
        reserve_plan: initialReservePlan,
        reserve: {
          request_type: initialRequestType,
          price_type: initialPriceType,
          plan_start_date: dateToStringWithFormat(
            defaultStartDate,
            'yyyy-MM-dd',
          ),
          plan_start_time: dateToStringWithFormat(defaultStartDate, 'HH:mm'),
          plan_end_date: dateToStringWithFormat(defaultEndDate, 'yyyy-MM-dd'),
          plan_end_time: dateToStringWithFormat(defaultEndDate, 'HH:mm'),
          casual_interview_flag: initialCasualInterviewFlag,
          interview_flg: initialInterviewFlg,
          tokyo_bs_tmp_use: initialTokyoBsTmpUse,
          support_style:
            initialCasualInterviewFlag === 1
              ? SUPPORT_STYLE_ONLINE
              : SUPPORT_STYLE_OFFLINE,
          person_at_home: initialPersonAtHome,
          comment: initialComment,
          security_option: {
            video_recording: {
              requested: initialVideoRecording,
            },
          },
        },
      },
    })

  // 入力内容
  const inputStartDate = watch('reserve.plan_start_date')
  const inputStartTime = watch('reserve.plan_start_time')
  const inputEndDate = watch('reserve.plan_end_date')
  const inputEndTime = watch('reserve.plan_end_time')
  const inputRequestType = watch('reserve.request_type')
  const inputPriceType = watch('reserve.price_type')
  const inputReservePlan = watch('reserve_plan')
  const inputCasualInterviewFlag = watch('reserve.casual_interview_flag')
  const inputInterviewFlag = watch('reserve.interview_flg')
  const inputTokyoBsTmpUse = watch('reserve.tokyo_bs_tmp_use')
  const inputSupportStyle = watch('reserve.support_style')
  const inputChildren = watch('child')
  const inputPersonAtHome = watch('reserve.person_at_home')
  const inputComment = watch('reserve.comment')
  const inputVideoRecording = watch(
    'reserve.security_option.video_recording.requested',
  )

  /*
   *
   * サポートの種類
   *
   */

  // 家庭教師の説明を表示するか
  const showTutorInformation = inputRequestType == 'request_tutor'

  // サポートの種類の変更
  const changeRequestTypeHandler = (requestType: IRequestType) => {
    setValue('reserve.request_type', requestType)

    if (requestType === REQUEST_TYPE_HOUSEKEEPING) {
      changeReserveTypeHandler(RESERVE_PLAN_SOPT)
    } else {
      changeReserveTypeHandler(RESERVE_PLAN_CASUAL_INTERVIEW)
    }
  }

  /*
   *
   * 予約の種類
   *
   */

  // 面談の表示を行うか
  const showInterview =
    needInterview &&
    availableInterviewRequest &&
    children.length > 0 &&
    isChildcare(inputRequestType)

  // 単発予約を行えるか
  const canSpotRequst =
    !showInterview ||
    [
      REQUEST_TYPE_DOULA,
      REQUEST_TYPE_TUTOR,
      REQUEST_TYPE_HOUSEKEEPING,
    ].includes(inputRequestType)

  // 一時預かりを行えるか
  const canTokyoBsTmpUse = ![
    REQUEST_TYPE_DOULA,
    REQUEST_TYPE_TUTOR,
    REQUEST_TYPE_HOUSEKEEPING,
  ].includes(inputRequestType)

  // 予約の種類の変更
  const changeReserveTypeHandler = (resrevePlan: IReservePlan) => {
    if (resrevePlan === RESERVE_PLAN_CASUAL_INTERVIEW) {
      setValue('reserve.casual_interview_flag', 1)
      setValue('reserve.interview_flg', 0)
      setValue('reserve.tokyo_bs_tmp_use', 0)
      // オンラインに設定
      changeSupportStyleHandler(SUPPORT_STYLE_ONLINE)
    } else if (resrevePlan === RESERVE_PLAN_INTERVIEW) {
      setValue('reserve.casual_interview_flag', 0)
      setValue('reserve.interview_flg', 1)
      setValue('reserve.tokyo_bs_tmp_use', 0)
      // オフラインに設定
      changeSupportStyleHandler(SUPPORT_STYLE_OFFLINE)
    } else if (resrevePlan === RESERVE_PLAN_SOPT) {
      setValue('reserve.casual_interview_flag', 0)
      setValue('reserve.interview_flg', 0)
      setValue('reserve.tokyo_bs_tmp_use', 0)
      // オフラインに設定
      changeSupportStyleHandler(SUPPORT_STYLE_OFFLINE)
    } else if (resrevePlan === RESERVE_PLAN_TOKYO_BS_TMP_USE) {
      setValue('reserve.casual_interview_flag', 0)
      setValue('reserve.interview_flg', 0)
      setValue('reserve.tokyo_bs_tmp_use', 1)
      // オフラインに設定
      changeSupportStyleHandler(SUPPORT_STYLE_OFFLINE)
    }
  }

  /*
   *
   * オンラインサポート
   *
   */
  const showSupportStyle = !inputCasualInterviewFlag
  const showOnlineSupportInformation =
    inputCasualInterviewFlag === 1 || inputSupportStyle === SUPPORT_STYLE_ONLINE
  const changeSupportStyleHandler = (supportStyle: ISupportStyle) => {
    setValue('reserve.support_style', supportStyle)
  }

  /*
   *
   * 具体的な内容
   *
   */
  const clickExampleCommentHandler = (comment: string) => {
    setValue('reserve.comment', comment)
  }

  /*
   *
   * 東京BS
   *
   */
  const showTokyoBsInformation =
    inputTokyoBsTmpUse === 1 &&
    [
      REQUEST_TYPE_PICKUP_ONLY,
      REQUEST_TYPE_SICK,
      REQUEST_TYPE_HANDICAP,
      REQUEST_TYPE_ILLNESS,
    ].includes(inputRequestType)

  const showTokyoBsCaution =
    inputTokyoBsTmpUse === 1 &&
    Object.keys(inputChildren).filter((id) => inputChildren[id]).length > 1

  /*
   *
   * 家事支援
   *
   */
  const showHousekeepingSubsidyForm =
    showHousekeepingSubsidy && inputRequestType === REQUEST_TYPE_HOUSEKEEPING

  /*
   *
   * サブミット制御
   *
   */
  const ref = React.useRef<HTMLFormElement>(undefined)
  const submitHandler = () => {
    // 2重送信を防ぐためにボタンを無効化する
    setCanSubmit(false)
    ref.current.submit()
  }

  const cancelHandler = () => {
    location.href = cancelURL
  }

  return (
    <>
      <Presenter
        defaultStartDate={defaultStartDate}
        defaultEndDate={defaultEndDate}
        dateRange={dateRange}
        hourRange={hourRange}
        showTutorInformation={showTutorInformation}
        showInterview={showInterview}
        canSpotRequst={canSpotRequst}
        canTokyoBsTmpUse={canTokyoBsTmpUse}
        showHousekeepingSubsidy={showHousekeepingSubsidyForm}
        disabledHousekeepingSubsidy={disabledHousekeepingSubsidy}
        showSupportStyle={showSupportStyle}
        children={children}
        showOnlineSupportInformation={showOnlineSupportInformation}
        showTokyoBsInformation={showTokyoBsInformation}
        showTokyoBsCaution={showTokyoBsCaution}
        isInterview={inputCasualInterviewFlag || inputInterviewFlag}
        inputRequestType={inputRequestType}
        inputCasualInterviewFlag={inputCasualInterviewFlag}
        inputInterviewFlag={inputInterviewFlag}
        inputTokyoBsTmpUse={inputTokyoBsTmpUse}
        inputStartDateString={inputStartDate}
        inputSupportStyle={inputSupportStyle}
        inputComment={inputComment}
        canSubmit={canSubmit}
        register={register}
        changeRequestTypeHandler={changeRequestTypeHandler}
        changeReserveTypeHandler={changeReserveTypeHandler}
        changeSupportStyleHandler={changeSupportStyleHandler}
        clickExampleCommentHandler={clickExampleCommentHandler}
        submitHandler={handleSubmit(submitHandler)}
        cancelHandler={cancelHandler}
        {...props}
      />
      <form action={submitURL} acceptCharset="UTF-8" method="post" ref={ref}>
        <input
          type="hidden"
          name="authenticity_token"
          value={authenticityToken}
          autoComplete="off"
        />
        <input
          type="hidden"
          name="reserve[plan_start_date]"
          value={inputStartDate}
        />
        <input
          type="hidden"
          name="reserve[plan_start_time]"
          value={inputStartTime}
        />
        <input
          type="hidden"
          name="reserve[plan_end_date]"
          value={inputEndDate}
        />
        <input
          type="hidden"
          name="reserve[plan_end_time]"
          value={inputEndTime}
        />
        <input
          type="hidden"
          name="reserve[request_type]"
          value={inputRequestType}
        />
        {showHousekeepingSubsidyForm && (
          <input
            type="hidden"
            name="reserve[price_type]"
            value={inputPriceType}
          />
        )}
        <input type="hidden" name="reserve_plan" value={inputReservePlan} />
        {inputRequestType !== REQUEST_TYPE_HOUSEKEEPING && (
          <>
            <input
              type="hidden"
              name="reserve[interview_flg]"
              value={inputInterviewFlag}
            />
            <input
              type="hidden"
              name="reserve[casual_interview_flag]"
              value={inputCasualInterviewFlag}
            />
            <input
              type="hidden"
              name="reserve[tokyo_bs_tmp_use]"
              value={inputTokyoBsTmpUse}
            />
          </>
        )}
        <input
          type="hidden"
          name="reserve[support_style]"
          value={inputSupportStyle}
        />
        {inputRequestType !== REQUEST_TYPE_HOUSEKEEPING &&
          inputChildren.map((id) => (
            <input type="hidden" name={`child[${id}]`} value={1} />
          ))}
        {inputRequestType === REQUEST_TYPE_HANDICAP && (
          <input
            type="hidden"
            name="reserve[person_at_home]"
            value={inputPersonAtHome}
          />
        )}
        <input
          type="hidden"
          name="reserve_comment[comment]"
          value={inputComment}
        />
        {Number(inputVideoRecording) === 1 && (
          <input
            type="hidden"
            name="reserve[security_option][video_recording][requested]"
            value={1}
          />
        )}
      </form>
    </>
  )
}

export default TimeTmpHolding
